import { AbpLocalStorageService } from '@abp/ng.core';
import { Injectable } from '@angular/core';
import { isFilterStateKey } from 'src/app/constants/filter-page-keys';

/** Id of the current browser session, kept in sessionStorage (dies with the tab). */
const SESSION_ID_KEY = 'ldm.session.id';

/**
 * Last session id published to localStorage, with the epoch it was published at. It exists for
 * tabs that start at the SAME time (browser "continue where you left off", two links opened in a
 * row): none of them can answer a handshake yet, so without this they would each mint their own
 * id and then delete each other's records. Deliberately NOT an `ldm.filters.*` key, so it
 * survives clearAll().
 */
const SESSION_CURRENT_KEY = 'ldm.session.current';

/** Transient localStorage keys used by the cross-tab handshake. */
const HANDSHAKE_REQUEST_KEY = 'ldm.session.handshake.request';
const HANDSHAKE_RESPONSE_KEY = 'ldm.session.handshake.response';

/** How long a fresh tab waits for another portal tab to answer. */
const HANDSHAKE_TIMEOUT_MS = 150;

/**
 * How recent the published session has to be for a tab that got no answer to adopt it.
 * Long enough to cover tabs booting together, far too short to bridge a browser restart.
 */
const SESSION_ADOPT_WINDOW_MS = 5000;

interface HandshakeMessage {
  nonce: string;
  sessionId?: string;
}

interface PublishedSession {
  id: string;
  at: number;
}

/**
 * Gives the filter persistence a "browser session" scope while the records themselves
 * live in localStorage (so they are shared between tabs and survive a refresh).
 *
 * On startup a tab either:
 *  - finds its own id in sessionStorage  -> it was merely reloaded, filters are kept;
 *  - or asks the other tabs for theirs   -> the `storage` event fires ONLY in other tabs,
 *    so an answer proves another portal tab is open and its session id is adopted;
 *  - or gets no answer but finds a session published moments ago -> another tab is booting
 *    right now, its id is adopted so both converge (see SESSION_CURRENT_KEY);
 *  - or gets no answer at all            -> this is a brand new browser session, a new id
 *    is generated and every persisted filter record is wiped.
 *
 * Every tab keeps listening for handshake requests for its whole lifetime so it can answer
 * tabs opened later. All storage access is best effort: a failure must never break bootstrap.
 */
@Injectable({ providedIn: 'root' })
export class BrowserSessionService {
  private _sessionId = '';

  constructor(private readonly localStorage: AbpLocalStorageService) {}

  get sessionId(): string {
    return this._sessionId;
  }

  initialize(): Promise<void> {
    try {
      if (typeof window === 'undefined') {
        this._sessionId = this.createId();
        return Promise.resolve();
      }

      this.listenForHandshakeRequests();

      const existingId = this.readSessionStorage(SESSION_ID_KEY);
      if (existingId) {
        // Same tab, plain reload: keep the session and therefore the filters.
        this._sessionId = existingId;
        return Promise.resolve();
      }

      return this.adoptSessionFromOtherTabs();
    } catch {
      this._sessionId = this.createId();
      return Promise.resolve();
    }
  }

  private adoptSessionFromOtherTabs(): Promise<void> {
    return new Promise<void>(resolve => {
      const nonce = this.createId();
      let settled = false;

      const finish = (adoptedSessionId: string | null) => {
        if (settled) return;
        settled = true;

        try {
          window.removeEventListener('storage', onResponse);
          this.localStorage.removeItem(HANDSHAKE_REQUEST_KEY);
          this.localStorage.removeItem(HANDSHAKE_RESPONSE_KEY);
        } catch {
          // ignore: cleaning up the transient keys is best effort
        }

        if (adoptedSessionId) {
          this._sessionId = adoptedSessionId;
        } else {
          // Nobody answered. A session published moments ago means another tab is booting at
          // the same time (and has already wiped, if it had to), so join it instead of racing.
          const bootingSessionId = this.readRecentlyPublishedSession();

          if (bootingSessionId) {
            this._sessionId = bootingSessionId;
          } else {
            // New browser session: the previous filters must not come back.
            this._sessionId = this.createId();
            this.clearPersistedFilters();
          }
        }

        this.publishSession(this._sessionId);
        this.writeSessionStorage(SESSION_ID_KEY, this._sessionId);
        resolve();
      };

      const onResponse = (event: StorageEvent) => {
        if (event.key !== HANDSHAKE_RESPONSE_KEY || !event.newValue) return;

        const answer = this.parseHandshake(event.newValue);
        if (answer?.nonce === nonce && answer.sessionId) {
          finish(answer.sessionId);
        }
      };

      try {
        window.addEventListener('storage', onResponse);
        this.localStorage.setItem(
          HANDSHAKE_REQUEST_KEY,
          JSON.stringify({ nonce } as HandshakeMessage),
        );
      } catch {
        finish(null);
        return;
      }

      setTimeout(() => finish(null), HANDSHAKE_TIMEOUT_MS);
    });
  }

  /** Answers handshake requests coming from tabs opened later in the same session. */
  private listenForHandshakeRequests(): void {
    window.addEventListener('storage', event => {
      if (event.key !== HANDSHAKE_REQUEST_KEY || !event.newValue || !this._sessionId) return;

      const request = this.parseHandshake(event.newValue);
      if (!request?.nonce) return;

      try {
        this.localStorage.setItem(
          HANDSHAKE_RESPONSE_KEY,
          JSON.stringify({ nonce: request.nonce, sessionId: this._sessionId } as HandshakeMessage),
        );
      } catch {
        // ignore: the asking tab simply falls back to a new session
      }
    });
  }

  private clearPersistedFilters(): void {
    try {
      const keys: string[] = [];
      for (let index = 0; index < this.localStorage.length; index++) {
        const key = this.localStorage.key(index);
        if (isFilterStateKey(key)) {
          keys.push(key);
        }
      }
      keys.forEach(key => this.localStorage.removeItem(key));
    } catch {
      // ignore: private mode / disabled storage
    }
  }

  /** Session id published within the adopt window, or null when there is none / it is stale. */
  private readRecentlyPublishedSession(): string | null {
    try {
      const raw = this.localStorage.getItem(SESSION_CURRENT_KEY);
      if (!raw) return null;

      const published = JSON.parse(raw) as PublishedSession;
      const isRecent =
        !!published &&
        typeof published.id === 'string' &&
        typeof published.at === 'number' &&
        Date.now() - published.at <= SESSION_ADOPT_WINDOW_MS;

      return isRecent ? published.id : null;
    } catch {
      return null;
    }
  }

  private publishSession(sessionId: string): void {
    try {
      this.localStorage.setItem(
        SESSION_CURRENT_KEY,
        JSON.stringify({ id: sessionId, at: Date.now() } as PublishedSession),
      );
    } catch {
      // ignore: private mode / disabled storage
    }
  }

  private parseHandshake(raw: string): HandshakeMessage | null {
    try {
      const parsed = JSON.parse(raw) as HandshakeMessage;
      return parsed && typeof parsed.nonce === 'string' ? parsed : null;
    } catch {
      return null;
    }
  }

  private readSessionStorage(key: string): string | null {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private writeSessionStorage(key: string, value: string): void {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // ignore: private mode / disabled storage
    }
  }

  private createId(): string {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}
